import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Upload, Search, ArrowLeft, User, Users, LogOut, ChevronDown, Settings } from 'lucide-react';
import Sidebar from './components/Sidebar';
import Home from './components/Home';
import Library from './components/Library';
import Chats from './components/Chats';
import Summaries from './components/Summaries';
import DocumentAnnotator from './components/DocumentAnnotator';
import Deals from './components/Deals';
import Contacts from './components/Contacts';
import AIFindings from './components/AIFindings';
import FindingsNotes from './components/FindingsNotes';

import DocumentUpload from './components/DocumentUpload';
import DiligenceTracker from './components/DiligenceTracker';
import Pipeline from './components/Pipeline';
import SettingsPage from './components/SettingsPage';
import GuidePage from './components/GuidePage';
import CRM from './components/CRM';
import DealAnalytics from './components/DealAnalytics';
import ErrorBoundary from './components/ErrorBoundary';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import OnboardingPage from './pages/OnboardingPage';
import PlatformAdmin from './pages/PlatformAdmin';
import InvitePage from './pages/InvitePage';
import AuthCallback from './pages/AuthCallback';
import TeamSettings from './components/TeamSettings';
import axios from 'axios';
import { supabase, buildCurrentUser, getOrgById } from './lib/supabase';

import './App.css';
import './styles/dealnexa-theme.css';

// Use environment variable; empty string means "same host" (React proxy handles it in dev)
export const API_URL = process.env.REACT_APP_API_URL || '';

// Enable cookies for cross-origin requests (required for embed session)
axios.defaults.withCredentials = true;

// Main App Content Component (for authenticated users)
function AppContent({ currentUser, onLogout, isEmbed: propIsEmbed }) {
  const [currentView, setCurrentView] = useState('home');
  const [selectedDocumentId, setSelectedDocumentId] = useState(null);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [deals, setDeals] = useState([]);
  const [dealToOpen, setDealToOpen] = useState(null);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [orgData, setOrgData] = useState(null);
  
  // Embed mode state
  const [isEmbed, setIsEmbed] = useState(false);
  const [embedLoading, setEmbedLoading] = useState(false);
  const [embedError, setEmbedError] = useState(null);
  const [embedDeal, setEmbedDeal] = useState(null);
  const [embedReturnUrl, setEmbedReturnUrl] = useState('');

  // Load org data when user has an org
  useEffect(() => {
    if (currentUser?.orgId) {
      // Use org data from user if available, otherwise fetch from API
      if (currentUser.orgName) {
        setOrgData({
          id: currentUser.orgId,
          name: currentUser.orgName,
          type: 'vc' // Default type, can be updated from API
        });
      }
      // Also try to fetch full org details from API
      getOrgById(currentUser.orgId).then(org => { 
        if (org) setOrgData(org); 
      }).catch(() => {});
    }
  }, [currentUser?.orgId, currentUser?.orgName]);

  // Audit log helper — fire-and-forget
  const logAudit = useCallback((action, resource, details) => {
    if (!currentUser) return;
    axios.post(`${API_URL}/api/audit/log`, {
      action,
      resource: resource || '',
      details: details || '',
      userName: currentUser.name || currentUser.email,
    }).catch(() => {});
  }, [currentUser]);

  // Fetch deals — callable from child components too
  const fetchDeals = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/deals`);
      setDeals(response.data || []);
    } catch (error) {
      console.error('Failed to fetch deals:', error);
    }
  }, []);

  useEffect(() => { fetchDeals(); }, [fetchDeals]);

  // Check for embed mode on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const isEmbedMode = propIsEmbed || urlParams.get('embed') === 'true' || window.location.pathname === '/embed' || urlParams.get('token') !== null;
    
    if (isEmbedMode) {
      setIsEmbed(true);
      handleEmbedAuth();
    }
    
    // Listen for embed config from parent (if in iframe)
    if (window.DEALNEXA_EMBED) {
      setEmbedReturnUrl(window.DEALNEXA_EMBED.returnUrl || '');
    }
  }, []);
  
  // Handle embed authentication
  const handleEmbedAuth = async () => {
    const token = new URLSearchParams(window.location.search).get('token');
    const externalDealId = new URLSearchParams(window.location.search).get('externalDealId');
    
    // First, try to use existing session (for "Open in new tab" scenarios)
    // by checking if we can fetch deals (which requires valid session)
    setEmbedLoading(true);
    try {
      // Try to fetch deals - this will only succeed if session cookie is valid
      const dealsResponse = await axios.get(`${API_URL}/api/deals`);
      
      // If we got here, session exists and is valid
      if (dealsResponse.data && dealsResponse.data.length > 0) {
        // Find the deal matching the externalDealId from URL
        let targetDeal = null;
        if (externalDealId) {
          targetDeal = dealsResponse.data.find(d => d.externalDealId === externalDealId);
        }
        
        // If we found the matching deal in the current session, use it
        if (targetDeal) {
          console.log('[Embed] Using existing session for deal:', targetDeal.name);
          setEmbedDeal(targetDeal);
          setSelectedDeal(targetDeal);
          setCurrentView('home');
          
          // Notify parent
          if (window.parent !== window) {
            window.parent.postMessage({
              type: 'dealnexa.ready',
              dealId: targetDeal.id
            }, '*');
          }
          setEmbedLoading(false);
          return; // Skip SSO exchange, we have a valid session for this deal
        }
        
        // Deal not found in current session - we need to do SSO exchange
        // if we have a token for the new deal
        if (externalDealId && token) {
          console.log('[Embed] Deal not in session, doing SSO exchange for:', externalDealId);
          // Continue to SSO exchange below (don't return)
        } else {
          // No token to exchange, use the first deal from session as fallback
          console.log('[Embed] No token for new deal, using session deal:', dealsResponse.data[0].name);
          setEmbedDeal(dealsResponse.data[0]);
          setSelectedDeal(dealsResponse.data[0]);
          setCurrentView('home');
          
          if (window.parent !== window) {
            window.parent.postMessage({
              type: 'dealnexa.ready',
              dealId: dealsResponse.data[0].id
            }, '*');
          }
          setEmbedLoading(false);
          return;
        }
      }
    } catch (sessionError) {
      // No valid session, continue to SSO exchange below
      console.log('[Embed] No valid session, trying SSO exchange...');
    }
    
    // If we have a token and no valid session, exchange it
    if (token) {
      try {
        const response = await axios.post(`${API_URL}/api/partner/sso/exchange`, {
          token,
          externalDealId
        });
        
        // Load the deal
        const dealResponse = await axios.get(`${API_URL}/api/partner/deal/${response.data.dealId}`);
        setEmbedDeal(dealResponse.data);
        setSelectedDeal(dealResponse.data);
        
        // Go to home page to show full platform
        setCurrentView('home');
        
        // Notify parent
        if (window.parent !== window) {
          window.parent.postMessage({
            type: 'dealnexa.ready',
            dealId: response.data.dealId,
            externalDealId: dealResponse.data.externalDealId
          }, '*');
        }
        
      } catch (error) {
        console.error('Embed auth failed:', error);
        setEmbedError(error.response?.data?.error || 'Authentication failed');
      } finally {
        setEmbedLoading(false);
      }
    } else {
      // No token and no session
      setEmbedError('No authentication token provided');
      setEmbedLoading(false);
    }
  };

  // Handle navigation
  const handleNavigate = (view) => {
    setCurrentView(view);
    // Reset selections when navigating away
    if (view !== 'annotations') {
      setSelectedDocumentId(null);
    }
  };

  // Handle navigate to deal from contacts
  const handleNavigateToDeal = (dealId) => {
    // Find the deal and set it as selected
    const deal = deals.find(d => d.id === dealId);
    if (deal) {
      setSelectedDeal(deal);
      setCurrentView('deals');
    }
  };

  // Handle opening a document for annotation
  const handleOpenAnnotate = (documentId) => {
    setSelectedDocumentId(documentId);
    setCurrentView('annotations');
  };

  // Handle viewing Diligence Tracker for a deal
  const handleViewDiligenceTracker = (deal) => {
    setSelectedDeal(deal);
    setCurrentView('diligencetracker');
  };

  // Handle analysis completion from upload
  const handleAnalysisComplete = () => {
    console.log('Analysis complete');
    
    // Notify parent if in embed mode
    if (isEmbed && window.parent !== window && selectedDeal) {
      window.parent.postMessage({
        type: 'dealnexa.analysis.complete',
        dealId: selectedDeal.id,
        externalDealId: selectedDeal.externalDealId
      }, '*');
    }
  };

  // Handle back button in embed mode
  const handleEmbedBack = () => {
    if (embedReturnUrl) {
      window.location.href = embedReturnUrl;
    } else if (window.parent !== window) {
      window.parent.postMessage({ type: 'dealnexa.back' }, '*');
    }
  };

  // Render the current view
  const renderView = () => {
    switch (currentView) {
      case 'home':
        return (
          <Home 
            onNavigate={handleNavigate} 
            onViewDiligenceTracker={handleViewDiligenceTracker}
            onSelectDeal={setSelectedDeal}
          />
        );
      case 'library':
        return <Library onOpenAnnotate={handleOpenAnnotate} />;
      case 'chats':
        return <Chats />;
      case 'summaries':
        return <Summaries />;
      case 'annotations':
        return <DocumentAnnotator documentId={selectedDocumentId} dealId={selectedDeal?.id || embedDeal?.id} />;
      case 'contacts':
        return <Contacts onNavigateToDeal={handleNavigateToDeal} />;
      case 'findings':
        return <FindingsNotes />;
      case 'pipeline':
        return (
          <Pipeline
            onNavigate={handleNavigate}
            deals={deals}
            setDeals={setDeals}
            onOpenDeal={(deal) => { setDealToOpen(deal); setCurrentView('deals'); }}
          />
        );
      case 'deals':
        return (
          <Deals
            onViewDiligenceTracker={handleViewDiligenceTracker}
            deals={deals}
            setDeals={setDeals}
            initialDeal={dealToOpen}
            onInitialDealConsumed={() => setDealToOpen(null)}
            currentUser={currentUser}
            logAudit={logAudit}
          />
        );
      case 'aifindings':
        return <AIFindings selectedDeal={selectedDeal || embedDeal} />;
      case 'diligencetracker':
        return <DiligenceTracker selectedDeal={selectedDeal || embedDeal} />;
      case 'settings':
        return (
          <SettingsPage
            currentUser={currentUser}
            orgData={orgData}
            onNavigate={handleNavigate}
          />
        );
      case 'crm':
        return (
          <CRM
            deals={deals}
            setDeals={setDeals}
            logAudit={logAudit}
          />
        );
      case 'analytics':
        return <DealAnalytics currentUser={currentUser} />;
      case 'guide':
        return <GuidePage />;
      case 'upload':
        return (
          <div className="upload-view">
            <div className="page-header">
              <div className="page-title">
                <div className="page-title-icon"><Upload size={20} /></div>
                <h1>Upload Documents</h1>
              </div>
            </div>
            <DocumentUpload onAnalysisComplete={handleAnalysisComplete} />
          </div>
        );
      default:
        return (
          <Home 
            onNavigate={handleNavigate} 
            onViewDiligenceTracker={handleViewDiligenceTracker}
          />
        );
    }
  };

  // Embed mode loading state
  if (isEmbed && embedLoading) {
    return (
      <div className="embed-loading">
        <div className="spinner"></div>
        <p>Loading DealNexa...</p>
      </div>
    );
  }

  // Embed mode error state
  if (isEmbed && embedError) {
    return (
      <div className="embed-error">
        <h2>Failed to load</h2>
        <p>{embedError}</p>
        {embedReturnUrl && (
          <button onClick={handleEmbedBack} className="btn-primary">
            <ArrowLeft size={16} /> Go Back
          </button>
        )}
      </div>
    );
  }

  // Embed mode - show full platform with navigation
  if (isEmbed) {
    return (
      <div className="app embed-mode">
        {/* Embed header with navigation */}
        <header className="embed-header">
          <div className="embed-header-left">
            {embedReturnUrl && (
              <button onClick={handleEmbedBack} className="embed-back-btn">
                <ArrowLeft size={16} /> Back
              </button>
            )}
            <span className="embed-deal-name">
              {embedDeal?.name || selectedDeal?.name || 'DealNexa'}
            </span>
          </div>
          
          {/* Navigation tabs */}
          <nav className="embed-nav">
            <button 
              className={`embed-nav-btn ${currentView === 'home' ? 'active' : ''}`}
              onClick={() => handleNavigate('home')}
            >
              Dashboard
            </button>
            <button 
              className={`embed-nav-btn ${currentView === 'deals' ? 'active' : ''}`}
              onClick={() => handleNavigate('deals')}
            >
              Deals
            </button>
            <button 
              className={`embed-nav-btn ${currentView === 'library' ? 'active' : ''}`}
              onClick={() => handleNavigate('library')}
            >
              Library
            </button>
            <button 
              className={`embed-nav-btn ${currentView === 'diligencetracker' ? 'active' : ''}`}
              onClick={() => handleNavigate('diligencetracker')}
            >
              Tracker
            </button>
            <button 
              className={`embed-nav-btn ${currentView === 'aifindings' ? 'active' : ''}`}
              onClick={() => handleNavigate('aifindings')}
            >
              AI Findings
            </button>
            <button 
              className={`embed-nav-btn ${currentView === 'annotations' ? 'active' : ''}`}
              onClick={() => handleNavigate('annotations')}
            >
              Annotations
            </button>
            <button 
              className={`embed-nav-btn ${currentView === 'chats' ? 'active' : ''}`}
              onClick={() => handleNavigate('chats')}
            >
              Chat
            </button>
            <button 
              className={`embed-nav-btn ${currentView === 'upload' ? 'active' : ''}`}
              onClick={() => handleNavigate('upload')}
            >
              Upload
            </button>
          </nav>
          
          <div className="embed-header-right">
            <span className="embed-badge">Due Diligence</span>
          </div>
        </header>

        {/* Page Content */}
        <main className="embed-content">
          <ErrorBoundary label="This view">
            {renderView()}
          </ErrorBoundary>
        </main>
      </div>
    );
  }

  // Full app mode
  return (
    <div className="app">
      <Sidebar activeView={currentView} onNavigate={handleNavigate} orgName={orgData?.name} />
      
      <div className="main-wrapper">
        {/* Top Header */}
        <header className="top-header">
          <div className="sidebar-header-mobile">
            <span className="sidebar-logo">{orgData?.name ? orgData.name.slice(0, 2).toUpperCase() : 'DN'}</span>
            <span className="sidebar-brand">{orgData?.name || 'DealNexa'}</span>
          </div>
          
          <div className="header-search">
            <div className="search-input-wrapper">
              <span className="search-icon"><Search size={16} /></span>
              <input 
                type="text" 
                className="search-input" 
                placeholder="Search documents, deals, annotations..."
              />
            </div>
          </div>
          
          <div className="header-actions">
            {currentUser && (
              <div className="user-menu-container">
                <button 
                  className="user-avatar-btn"
                  onClick={() => setShowUserDropdown(!showUserDropdown)}
                >
                  <span className="user-avatar">
                    {currentUser?.name?.[0]?.toUpperCase() || currentUser?.email?.[0]?.toUpperCase() || 'U'}
                  </span>
                  <ChevronDown size={14} />
                </button>
                
                {showUserDropdown && (
                  <>
                    <div className="user-dropdown-overlay" onClick={() => setShowUserDropdown(false)} />
                    <div className="user-dropdown">
                      <div className="user-dropdown-header">
                        <span className="user-dropdown-name">{currentUser.name || currentUser.email}</span>
                        <span className="user-dropdown-org">{currentUser.orgName || (currentUser.orgId ? 'Organization Member' : 'Personal Account')}</span>
                      </div>
                      <div className="user-dropdown-divider" />
                      <button
                        className="user-dropdown-item"
                        onClick={() => {
                          setShowUserDropdown(false);
                          setShowProfileModal(true);
                        }}
                      >
                        <User size={16} />
                        <span>Profile</span>
                      </button>
                      <button
                        className="user-dropdown-item"
                        onClick={() => {
                          setShowUserDropdown(false);
                          setShowMembersModal(true);
                        }}
                      >
                        <Users size={16} />
                        <span>Members</span>
                      </button>
                      <button
                        className="user-dropdown-item"
                        onClick={() => {
                          setShowUserDropdown(false);
                          handleNavigate('settings');
                        }}
                      >
                        <Settings size={16} />
                        <span>Settings</span>
                      </button>
                      <div className="user-dropdown-divider" />
                      <button
                        className="user-dropdown-item logout-item"
                        onClick={onLogout}
                      >
                        <LogOut size={16} />
                        <span>Logout</span>
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </header>

        {/* Page Content */}
        <main className="page-content">
          <ErrorBoundary label="This view">
            {renderView()}
          </ErrorBoundary>
        </main>
        
        {/* Profile Modal */}
        {showProfileModal && (
          <div className="modal-overlay" onClick={() => setShowProfileModal(false)}>
            <div className="modal-content profile-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Profile</h2>
                <button className="modal-close" onClick={() => setShowProfileModal(false)}>×</button>
              </div>
              <div className="modal-body">
                <div className="profile-section">
                  <div className="profile-avatar-large">
                    {currentUser?.name?.[0]?.toUpperCase() || currentUser?.email?.[0]?.toUpperCase() || 'U'}
                  </div>
                  <div className="profile-info">
                    <div className="profile-field">
                      <label>Name</label>
                      <span>{currentUser.name || 'Not set'}</span>
                    </div>
                    <div className="profile-field">
                      <label>Email</label>
                      <span>{currentUser.email}</span>
                    </div>
                    <div className="profile-field">
                      <label>Organization</label>
                      <span>{currentUser.orgName || (currentUser.orgId ? 'Organization Member' : 'Personal Account')}</span>
                    </div>
                    <div className="profile-field">
                      <label>User ID</label>
                      <span className="profile-id">{currentUser.id}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn-secondary" onClick={() => setShowProfileModal(false)}>Close</button>
              </div>
            </div>
          </div>
        )}
        
        {/* Team Settings Modal */}
        {showMembersModal && (
          <TeamSettings
            currentUser={currentUser}
            onClose={() => setShowMembersModal(false)}
          />
        )}
      </div>
    </div>
  );
}

// Main App Component with Router
function App() {
  const [currentUser, setCurrentUser] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const axiosInterceptorRef = useRef(null);

  // Check if this is an embed session (external platform user)
  const isEmbed = window.location.pathname === '/embed' ||
    new URLSearchParams(window.location.search).get('embed') === 'true' ||
    new URLSearchParams(window.location.search).get('token') !== null;

  // Attach / update the axios Authorization interceptor whenever accessToken changes
  useEffect(() => {
    if (axiosInterceptorRef.current !== null) {
      axios.interceptors.request.eject(axiosInterceptorRef.current);
    }
    axiosInterceptorRef.current = axios.interceptors.request.use((config) => {
      if (accessToken && !config.headers['Authorization']) {
        config.headers['Authorization'] = `Bearer ${accessToken}`;
      }
      return config;
    });
  }, [accessToken]);

  // Supabase session listener — single source of truth for auth state
  useEffect(() => {
    if (isEmbed) {
      setAuthChecked(true);
      return;
    }

    // Check for existing session
    const initSession = async () => {
      try {
        console.log('[Auth] Checking session...');
        const { data: { session }, error } = await supabase.auth.getSession();
        console.log('[Auth] Session result:', { hasSession: !!session, error });
        
        if (error) {
          console.error('[Auth] Session error:', error);
        }
        if (session?.user) {
          console.log('[Auth] Building user from session...');
          const user = await buildCurrentUser(session.user);
          setCurrentUser(user);
          setAccessToken(session.access_token);
        }
      } catch (err) {
        console.error('[Auth] Init session error:', err);
      } finally {
        console.log('[Auth] Setting authChecked to true');
        setAuthChecked(true);
      }
    };
    
    // Add timeout to prevent infinite loading
    const timeoutId = setTimeout(() => {
      console.warn('[Auth] Session check timed out, forcing authChecked');
      setAuthChecked(true);
    }, 3000);
    
    initSession().then(() => clearTimeout(timeoutId));

    // Listen for auth changes (login, logout, token refresh)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_OUT' || !session) {
        setCurrentUser(null);
        setAccessToken(null);
      } else if (session?.user) {
        const user = await buildCurrentUser(session.user);
        setCurrentUser(user);
        setAccessToken(session.access_token);
      }
    });

    return () => subscription.unsubscribe();
  }, [isEmbed]);

  // Called by LoginPage / SignupPage after successful auth
  const handleLogin = async (user, token, isNewUser = false) => {
    setCurrentUser(user);
    setAccessToken(token);
    // Sync with backend to establish cookie session for legacy API routes
    if (token) {
      try {
        await axios.post(`${API_URL}/api/auth/supabase-sync`, {}, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        });
      } catch { /* ignore */ }
    }
    // Only redirect to onboarding for new signups, not existing users logging in
    if (isNewUser && !user.orgId) {
      window.location.href = '/onboarding';
    }
  };

  // Called when OnboardingPage creates an org — refresh user profile
  const handleOrgCreated = async (org) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.user) {
      const user = await buildCurrentUser(session.user);
      // The buildCurrentUser already fetches orgName if org_id is set
      setCurrentUser(user);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch { /* ignore */ }
    await supabase.auth.signOut();
    setCurrentUser(null);
    setAccessToken(null);
  };

  if (!authChecked && !isEmbed) {
    return (
      <div className="app-loading">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <Router>
      <Routes>
        {/* Embed route - no login required */}
        <Route
          path="/embed"
          element={<AppContent currentUser={null} onLogout={handleLogout} isEmbed={true} />}
        />

        {/* Platform Admin - hidden route, no nav link */}
        <Route path="/platform-admin" element={<PlatformAdmin />} />

        {/* Public routes */}
        <Route path="/" element={currentUser ? <Navigate to="/home" /> : <LandingPage />} />
        <Route
          path="/login"
          element={currentUser ? <Navigate to="/home" /> : <LoginPage onLogin={handleLogin} />}
        />
        <Route
          path="/signup"
          element={currentUser ? <Navigate to="/home" /> : <SignupPage onLogin={handleLogin} />}
        />

        {/* Onboarding — accessible when logged in but no org yet */}
        <Route
          path="/onboarding"
          element={
            !currentUser ? (
              <Navigate to="/login" />
            ) : currentUser.orgId ? (
              <Navigate to="/home" />
            ) : (
              <OnboardingPage currentUser={currentUser} onOrgCreated={handleOrgCreated} />
            )
          }
        />

        {/* Auth callback from Google OAuth */}
        <Route path="/auth/callback" element={<AuthCallback />} />

        {/* Invite accept — public (handles its own auth) */}
        <Route
          path="/invite/:token"
          element={<InvitePage currentUser={currentUser} onLogin={handleLogin} />}
        />

        {/* Protected app routes */}
        <Route
          path="/*"
          element={
            currentUser ? (
              <AppContent currentUser={currentUser} onLogout={handleLogout} />
            ) : (
              <Navigate to="/login" />
            )
          }
        />
      </Routes>
    </Router>
  );
}

export default App;
